import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { getUserInfoAsync } from "State/Auth/UserInfo/GetUserInfoState";
import { JWT_KEY } from "Utils/AuthUtils";
import { areJwtClaimsEqual, hasSameJwtIdentity } from "Utils/JwtUtils";
import { Resources, useResource } from "Translations/Resources";
import { resetUser } from "State/Auth/AuthReducer";
import { useQueryClient } from "@tanstack/react-query";
import { prefetchUserInfo } from "Auth/Api/Queries/useUserInfoQuery";

const RESOURCES = Resources.Auth.StateSync;

type AuthStateSyncOptions = { showNotifications?: boolean };

export const useAuthStateSync = ({
  showNotifications = false,
}: AuthStateSyncOptions = {}) => {
  const dispatch = useDispatch();
  const { t } = useResource();
  const queryClient = useQueryClient();

  // biome-ignore lint/correctness/useExhaustiveDependencies: queryClient is referentially stable, no need to include
  useEffect(() => {
    const handleStorageChange = ({
      key,
      newValue: newToken,
      oldValue: oldToken,
    }: StorageEvent) => {
      const wasStorageCleared = key === null;

      if (wasStorageCleared) {
        handleLogout();
        return;
      }

      if (key !== JWT_KEY) {
        return;
      }

      if (!newToken && oldToken) {
        dispatch(resetUser({ onAfterReset: handleLogout }));
        return;
      }

      if (!newToken || newToken === oldToken) {
        return;
      }

      // Same user, same identity context — the backend re-mints the JWT (new
      // iat/exp) on every user-info call, so another tab writing a token for
      // the session we're already in is not a login. The API client reads the
      // token from localStorage per request, so there is nothing to sync;
      // treating this as a login used to ping-pong user-info calls (and the
      // "logged in from another tab" toast) between open tabs forever.
      if (oldToken && hasSameJwtIdentity(oldToken, newToken)) {
        if (!areJwtClaimsEqual(oldToken, newToken)) {
          // A non-identity claim changed elsewhere (e.g. active_region) —
          // refresh user info quietly, without the toast or loading overlay.
          prefetchUserInfo(queryClient);
          dispatch(getUserInfoAsync.request(undefined));
        }
        return;
      }

      handleLogin();
    };

    const handleLogin = () => {
      // React Query prefetch (for new consumers using useUserInfoQuery)
      prefetchUserInfo(queryClient);

      // Redux dispatch (keep for backwards compatibility during migration)
      dispatch(getUserInfoAsync.request({ withLoadingStatus: true }));

      if (showNotifications) {
        toast.info(t(RESOURCES.LoggedInFromAnotherTab));
      }
    };

    const handleLogout = () => {
      if (showNotifications) {
        toast.warning(t(RESOURCES.LoggedOutFromAnotherTab));
      }

      setTimeout(() => window.location.assign("/"), 1000);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch, showNotifications, t]);
};
